import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useRole } from "@/contexts/RoleContext";
import { useGrade } from "@/contexts/GradeContext";
import {
  teacherCockpit, teacherPrefsGet, teacherPrefsSet,
  teacherUnlock,
} from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import {
  Popover, PopoverContent, PopoverTrigger,
} from "@/components/ui/popover";
import {
  AlertOctagon, Moon, AlertTriangle, Sparkles,
  Printer, Settings, ChevronRight, Unlock, ChevronDown, ArrowLeft, BookCheck, Download, Users,
} from "lucide-react";
import TeacherNotifBell from "@/components/TeacherNotifBell";
import TestGeneratorModal from "@/components/TestGeneratorModal";
import StudentListPanel from "@/components/StudentListPanel";
import { ClassTopicsTab } from "@/components/ClassTopicsTab";
import { EngagementTab } from "@/components/EngagementTab";
import { toast } from "sonner";

/* ────────────────────────────────────────────────────────────────────────
 *  Zone configuration — colour palette and copy per spec.
 * ──────────────────────────────────────────────────────────────────────── */
const ZONE = {
  needs_attention: {
    key: "needs_attention",
    label: "Needs Attention",
    icon: AlertOctagon,
    barClass: "bg-rose-600",
    cardClass: "bg-rose-50 border-rose-200",
    pillClass: "bg-rose-100 text-rose-800",
    accent: "text-rose-700",
  },
  gone_quiet: {
    key: "gone_quiet",
    label: "Gone Quiet",
    icon: Moon,
    barClass: "bg-orange-500",
    cardClass: "bg-orange-50 border-orange-200",
    pillClass: "bg-orange-100 text-orange-800",
    accent: "text-orange-700",
  },
  struggling: {
    key: "struggling",
    label: "Struggling",
    icon: AlertTriangle,
    barClass: "bg-amber-500",
    cardClass: "bg-amber-50 border-amber-200",
    pillClass: "bg-amber-100 text-amber-800",
    accent: "text-amber-700",
  },
  flying: {
    key: "flying",
    label: "Flying",
    icon: Sparkles,
    barClass: "bg-emerald-600",
    cardClass: "bg-emerald-50 border-emerald-200",
    pillClass: "bg-emerald-100 text-emerald-800",
    accent: "text-emerald-700",
  },
};

const ZONE_ORDER = ["needs_attention", "gone_quiet", "struggling", "flying"];

/* Format the per-card metric depending on the student's individual preference. */
function metricLine(row, metric, zone) {
  // Gone Quiet zone: if metric is undefined/default, show last active date (status_plain)
  // Otherwise respect the individual card's metric setting
  if (zone === "gone_quiet" && !metric) {
    return row.status_plain;  // "Last seen X days ago — was working on..."
  }
  
  if (metric === "percent") {
    return `${row.overall_accuracy_pct}% correct overall`;
  }
  if (metric === "subtopics_done") {
    return `${row.established_count} of ${row.total_sub_topics} sub-topics done`;
  }
  return row.status_plain;
}

/* ────────────────────────────────────────────────────────────────────────
 *  Reusable per-card metric switcher (Settings cog → 3-option popover).
 * ──────────────────────────────────────────────────────────────────────── */
function MetricCog({ value, onChange, testId, stopPropagation = false }) {
  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          data-testid={testId}
          aria-label="Change display metric"
          onMouseDown={(e) => { 
            if (stopPropagation) {
              e.stopPropagation();
            }
          }}
          onClick={(e) => { 
            if (stopPropagation) {
              e.stopPropagation();
            }
          }}
          className="w-7 h-7 rounded-md hover:bg-slate-200/60 flex items-center justify-center text-slate-500 hover:text-slate-800 transition"
        >
          <Settings className="w-4 h-4" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-2" data-testid="metric-popover">
        <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide px-2 pt-1 pb-2">
          Card metric
        </div>
        {[
          { v: "plain",          label: "Plain English status" },
          { v: "percent",        label: "% correct overall"    },
          { v: "subtopics_done", label: "Sub-topics completed" },
        ].map(opt => (
          <button
            key={opt.v}
            data-testid={`metric-opt-${opt.v}`}
            onClick={(e) => {
              e.stopPropagation();  // Prevent card navigation
              onChange(opt.v);
            }}
            className={`w-full text-left text-sm rounded-md px-2 py-1.5 hover:bg-slate-100 ${value === opt.v ? "font-semibold text-slate-900" : "text-slate-700"}`}
          >
            {opt.label}{value === opt.v ? "  ✓" : ""}
          </button>
        ))}
      </PopoverContent>
    </Popover>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 *  Zone 1 — Needs Attention (full card + Unlock CTA)
 * ──────────────────────────────────────────────────────────────────────── */
function NeedsAttentionCard({ row, subject, metric, onUnlock, onMetricChange, grade }) {
  const stop = row.active_stops?.[0];
  return (
    <Card
      className="rounded-2xl p-4 bg-rose-50 border-2 border-rose-200 hover:border-rose-400 transition"
      data-testid={`zone-card-needs_attention-${row.learner_id}`}
    >
      <div className="flex items-start justify-between gap-3 mb-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-10 h-10 rounded-full bg-rose-600 text-white flex items-center justify-center font-teacher font-bold flex-shrink-0">
            {(row.name || "?").slice(0, 1)}
          </div>
          <div className="min-w-0">
            <Link
              to={`/teacher/student/${row.learner_id}`}
              state={{ from: `/teacher/cockpit/${grade}/${encodeURIComponent(subject)}` }}
              className="font-teacher text-lg font-bold text-slate-900 hover:underline block"
              data-testid={`card-name-${row.learner_id}`}
            >
              {row.name}
            </Link>
            <div className="text-xs text-rose-700 font-semibold uppercase tracking-wide">
              Stopped — needs unlock
            </div>
          </div>
        </div>
        <MetricCog value={metric} onChange={onMetricChange} testId={`cog-${row.learner_id}`} />
      </div>

      <div className="text-sm text-slate-800 mb-3">
        <span className="text-slate-500">Sub-topic where stopped:</span>{" "}
        <span className="font-semibold">{stop?.sub_topic || row.current_sub_topic || "—"}</span>
      </div>
      <div className="text-xs text-slate-600 mb-4" data-testid={`card-metric-${row.learner_id}`}>
        {metricLine(row, metric, "needs_attention")}
      </div>

      <div className="flex items-center gap-2">
        <Button
          size="sm"
          onClick={() => onUnlock(row)}
          data-testid={`unlock-btn-${row.learner_id}`}
          className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white text-xs h-8 px-3"
        >
          <Unlock className="w-3.5 h-3.5 mr-1" /> Unlock
        </Button>
        {stop?.stop_id && (
          <Link
            to={`/teacher/alert/${stop.stop_id}`}
            data-testid={`alert-detail-${row.learner_id}`}
            className="text-xs font-semibold text-rose-700 hover:underline"
          >
            View alert detail →
          </Link>
        )}
      </div>
    </Card>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 *  Zones 2 & 3 — Gone Quiet / Struggling (medium card, no inline CTA)
 * ──────────────────────────────────────────────────────────────────────── */
function MediumZoneCard({ row, zone, metric, onMetricChange, grade, subject }) {
  const cfg = ZONE[zone];
  const nav = useNavigate();
  const displayText = metricLine(row, metric, zone);
  
  const handleCardClick = (e) => {
    // Only navigate if not clicking on gear icon
    if (!e.target.closest('button[aria-label="Change display metric"]')) {
      nav(`/teacher/student/${row.learner_id}`, {
        state: { from: `/teacher/cockpit/${grade}/${encodeURIComponent(subject)}` }
      });
    }
  };
  
  return (
    <div
      onClick={handleCardClick}
      data-testid={`zone-card-${zone}-${row.learner_id}`}
      className={`rounded-xl p-4 ${cfg.cardClass} border-2 hover:border-slate-400 transition cursor-pointer`}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className={`w-9 h-9 rounded-full bg-white border ${cfg.cardClass.replace("bg-", "border-").split(" ")[1]} flex items-center justify-center font-teacher font-bold ${cfg.accent} flex-shrink-0`}>
            {(row.name || "?").slice(0, 1)}
          </div>
          <div className="flex-1 min-w-0">
            <div className="font-teacher font-semibold text-slate-900">{row.name}</div>
            <div className="text-xs text-slate-600" data-testid={`card-metric-${row.learner_id}`}>
              {displayText}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <MetricCog value={metric} onChange={onMetricChange} testId={`cog-${row.learner_id}`} stopPropagation={true} />
          <ChevronRight className="w-4 h-4 text-slate-400" />
        </div>
      </div>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 *  Zone 4 — Flying (condensed list row, expandable)
 * ──────────────────────────────────────────────────────────────────────── */
function FlyingRow({ row, metric, onMetricChange, grade, subject }) {
  const [expanded, setExpanded] = useState(false);
  return (
    <div
      data-testid={`zone-card-flying-${row.learner_id}`}
      className="rounded-lg bg-white border border-emerald-200 hover:border-emerald-400 transition overflow-hidden"
    >
      <button
        onClick={() => setExpanded(e => !e)}
        data-testid={`flying-toggle-${row.learner_id}`}
        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 text-left"
      >
        <div className="flex items-center gap-2.5 flex-1 min-w-0">
          <span className="w-2 h-2 rounded-full bg-emerald-500 flex-shrink-0" />
          <span className="font-teacher font-semibold text-slate-900">{row.name}</span>
          <span className="text-xs text-slate-500" data-testid={`card-metric-${row.learner_id}`}>
            · {metricLine(row, metric, "flying")}
          </span>
        </div>
        <ChevronDown className={`w-4 h-4 text-slate-400 transition ${expanded ? "rotate-180" : ""}`} />
      </button>
      {expanded && (
        <div className="px-3 pb-3 pt-1 border-t border-emerald-100 bg-emerald-50/50" data-testid={`flying-expanded-${row.learner_id}`}>
          <div className="text-xs text-slate-700 mb-2">
            <div>Sub-topic in flight: <span className="font-semibold">{row.current_sub_topic || "—"}</span></div>
            <div>Recent accuracy: <span className="font-semibold">{row.recent_accuracy_pct}%</span></div>
            <div>Sub-topics completed: <span className="font-semibold">{row.established_count} of {row.total_sub_topics}</span></div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/teacher/student/${row.learner_id}`}
              state={{ from: `/teacher/cockpit/${grade}/${encodeURIComponent(subject)}` }}
              className="text-xs font-semibold text-emerald-700 hover:underline"
              data-testid={`flying-detail-${row.learner_id}`}
            >
              Open full detail →
            </Link>
            <span className="flex-1" />
            <MetricCog value={metric} onChange={onMetricChange} testId={`cog-${row.learner_id}`} />
          </div>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 *  Zone wrapper — header + responsive grid; collapses entirely when empty.
 * ──────────────────────────────────────────────────────────────────────── */
function ZoneBlock({ zoneKey, rows, children }) {
  if (!rows || rows.length === 0) return null;
  const cfg = ZONE[zoneKey];
  const Icon = cfg.icon;
  return (
    <section className="mb-8" data-testid={`zone-${zoneKey}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className={`w-8 h-8 rounded-lg ${cfg.barClass} text-white flex items-center justify-center`}>
          <Icon className="w-4 h-4" />
        </div>
        <h2 className="font-teacher text-xl font-bold text-slate-900">
          {cfg.label.toUpperCase()}
        </h2>
        <span className={`text-xs font-bold px-2 py-1 rounded-md ${cfg.pillClass}`}>
          {rows.length}
        </span>
      </div>
      {children}
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 *  Page
 * ──────────────────────────────────────────────────────────────────────── */
export default function TeacherClassCockpit() {
  const { teacherId } = useRole();
  const { setSelectedGrade } = useGrade();
  const { grade, subject } = useParams();
  const nav = useNavigate();
  const subjectDecoded = decodeURIComponent(subject || "");
  const gradeNum = parseInt(grade, 10);

  // Sync header grade selector to this cockpit's grade
  useEffect(() => { setSelectedGrade(gradeNum); }, [gradeNum, setSelectedGrade]);

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  // Per-student metric state: { [learnerId]: metricValue }
  const [studentMetrics, setStudentMetrics] = useState({});
  const [showTestGenerator, setShowTestGenerator] = useState(false);
  const [showStudentList, setShowStudentList] = useState(false);
  const [activeTab, setActiveTab] = useState("overview"); // "overview" | "topics"

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [d] = await Promise.all([
        teacherCockpit(teacherId, gradeNum, subjectDecoded),
      ]);
      setData(d);
    } finally { setLoading(false); }
  }, [teacherId, gradeNum, subjectDecoded]);

  useEffect(() => { refresh(); }, [refresh]);
  
  // Listen for test generator open event from header
  useEffect(() => {
    const handleOpen = () => setShowTestGenerator(true);
    window.addEventListener('openTestGenerator', handleOpen);
    return () => window.removeEventListener('openTestGenerator', handleOpen);
  }, []);

  // Get metric for a specific student (undefined means default/plain for that zone)
  const getStudentMetric = (learnerId) => {
    return studentMetrics[learnerId];
  };

  // Set metric for a specific student
  const setStudentMetric = (learnerId, metric) => {
    setStudentMetrics(prev => ({
      ...prev,
      [learnerId]: metric
    }));
  };

  const onUnlock = async (row) => {
    const sub_topic = row.active_stops?.[0]?.sub_topic || row.current_sub_topic;
    if (!sub_topic) {
      toast.error("No sub-topic to unlock");
      return;
    }
    try {
      await teacherUnlock(teacherId, row.learner_id, subjectDecoded, sub_topic);
      toast.success(`Unlocked ${sub_topic} for ${row.name}`);
      refresh();
    } catch (e) {
      toast.error("Unlock failed");
    }
  };

  const onPrint = () => {
    window.open(`/teacher/print/${grade}/${encodeURIComponent(subjectDecoded)}`, "_blank");
  };

  if (loading || !data) {
    return (
      <div className="teacher-scope min-h-screen">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="text-slate-400 py-16 text-center">Loading cockpit…</div>
        </div>
      </div>
    );
  }

  const { zones, summary, chapters_complete } = data;
  const totalRendered = ZONE_ORDER.reduce((acc, k) => acc + (zones[k]?.length || 0), 0);

  return (
    <div className="teacher-scope min-h-screen">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="teacher-cockpit">
        {/* Header */}
        <div className="flex flex-wrap items-start justify-between gap-3 mb-6">
          <div className="min-w-0">
            <button
              onClick={() => nav(`/teacher/cockpit/${grade}`)}
              data-testid="cockpit-back"
              className="text-xs font-semibold text-slate-500 hover:text-slate-900 flex items-center gap-1 mb-2"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to subject picker
            </button>
            <h1 className="font-teacher text-3xl md:text-4xl font-bold text-slate-900" data-testid="cockpit-title">
              Grade {grade} · {subjectDecoded}
            </h1>
            <p className="text-slate-600 mt-1">
              {summary.total} learner{summary.total === 1 ? "" : "s"} ·
              {" "}<span className="font-semibold text-rose-700">{summary.needs_attention}</span> need attention ·
              {" "}<span className="font-semibold text-orange-700">{summary.gone_quiet}</span> gone quiet ·
              {" "}<span className="font-semibold text-amber-700">{summary.struggling}</span> struggling ·
              {" "}<span className="font-semibold text-emerald-700">{summary.flying}</span> flying
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button
              onClick={() => setShowStudentList(true)}
              variant="outline"
              size="sm"
              data-testid="students-btn"
              className="rounded-lg border-slate-300 hover:border-slate-900"
            >
              <Users className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Students</span>
            </Button>
            <Button
              onClick={onPrint}
              variant="outline"
              size="sm"
              data-testid="print-report-btn"
              className="rounded-lg border-slate-300 hover:border-slate-900"
            >
              <Printer className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Print Report</span>
            </Button>
            <Button
              onClick={onPrint}
              size="sm"
              data-testid="download-report-btn"
              className="rounded-lg bg-sky-600 hover:bg-sky-700 text-white"
            >
              <Download className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">Download PDF</span>
            </Button>
            <TeacherNotifBell teacherId={teacherId} />
          </div>
        </div>

        {/* Chapter complete banner */}
        {chapters_complete && chapters_complete.length > 0 && (
          <Card
            className="rounded-xl p-4 mb-6 bg-emerald-50 border-2 border-emerald-300"
            data-testid="chapter-complete-banner"
          >
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-600 text-white flex items-center justify-center flex-shrink-0">
                <BookCheck className="w-5 h-5" />
              </div>
              <div className="flex-1">
                <div className="font-teacher font-bold text-emerald-900">
                  Chapter report ready
                </div>
                {chapters_complete.map(c => (
                  <div key={c.topic} className="text-sm text-emerald-800" data-testid={`chapter-complete-${c.topic}`}>
                    <strong>{c.topic}</strong> complete for all students.
                  </div>
                ))}
                <button
                  onClick={onPrint}
                  data-testid="chapter-complete-print"
                  className="text-xs font-semibold text-emerald-700 hover:underline mt-1"
                >
                  View and print chapter summary →
                </button>
              </div>
            </div>
          </Card>
        )}

        {/* Tab bar */}
        <div className="flex gap-0 mb-6 border-b border-slate-200" data-testid="cockpit-tabs">
          <button
            onClick={() => setActiveTab("overview")}
            data-testid="tab-overview"
            className={`px-5 py-2.5 text-sm font-semibold transition border-b-2 -mb-px ${
              activeTab === "overview"
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            Class Overview
          </button>
          <button
            onClick={() => setActiveTab("topics")}
            data-testid="tab-topics"
            className={`px-5 py-2.5 text-sm font-semibold transition border-b-2 -mb-px ${
              activeTab === "topics"
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            Topics
          </button>
          <button
            onClick={() => setActiveTab("engagement")}
            data-testid="tab-engagement"
            className={`px-5 py-2.5 text-sm font-semibold transition border-b-2 -mb-px ${
              activeTab === "engagement"
                ? "border-slate-900 text-slate-900"
                : "border-transparent text-slate-500 hover:text-slate-800"
            }`}
          >
            Engagement
          </button>
        </div>

        {/* Topics tab */}
        {activeTab === "topics" && (
          <ClassTopicsTab
            teacherId={teacherId}
            grade={gradeNum}
            subject={subjectDecoded}
          />
        )}

        {/* Engagement tab */}
        {activeTab === "engagement" && (
          <EngagementTab teacherId={teacherId} grade={gradeNum} />
        )}

        {/* Class Overview tab content */}
        {activeTab === "overview" && (<>

        {/* Empty-class fallback */}
        {totalRendered === 0 && summary.not_started === 0 && (
          <Card className="rounded-xl p-10 text-center text-slate-500 bg-white border border-slate-200" data-testid="cockpit-empty">
            No learners assigned. Add learners to the class to populate the cockpit.
          </Card>
        )}
        {totalRendered === 0 && summary.not_started > 0 && (
          <Card className="rounded-xl p-10 text-center bg-slate-50 border border-slate-200" data-testid="cockpit-nothing-yet">
            <div className="text-slate-700 font-semibold">All quiet on this subject.</div>
            <div className="text-slate-500 text-sm mt-1">
              {summary.not_started} learner{summary.not_started === 1 ? " hasn't" : "s haven't"} started {subjectDecoded} yet.
            </div>
          </Card>
        )}

        {/* Zone 1 — Needs Attention (full cards, 1-col on mobile / 2-col desktop) */}
        <ZoneBlock zoneKey="needs_attention" rows={zones.needs_attention}>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            {zones.needs_attention.map(r => (
              <NeedsAttentionCard
                key={r.learner_id}
                row={r}
                subject={subjectDecoded}
                grade={grade}
                metric={getStudentMetric(r.learner_id)}
                onUnlock={onUnlock}
                onMetricChange={(m) => setStudentMetric(r.learner_id, m)}
              />
            ))}
          </div>
        </ZoneBlock>

        {/* Zone 2 — Gone Quiet */}
        <ZoneBlock zoneKey="gone_quiet" rows={zones.gone_quiet}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {zones.gone_quiet.map(r => (
              <MediumZoneCard
                key={r.learner_id}
                row={r}
                zone="gone_quiet"
                grade={grade}
                subject={subjectDecoded}
                metric={getStudentMetric(r.learner_id)}
                onMetricChange={(m) => setStudentMetric(r.learner_id, m)}
              />
            ))}
          </div>
        </ZoneBlock>

        {/* Zone 3 — Struggling */}
        <ZoneBlock zoneKey="struggling" rows={zones.struggling}>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {zones.struggling.map(r => (
              <MediumZoneCard
                key={r.learner_id}
                row={r}
                zone="struggling"
                grade={grade}
                subject={subjectDecoded}
                metric={getStudentMetric(r.learner_id)}
                onMetricChange={(m) => setStudentMetric(r.learner_id, m)}
              />
            ))}
          </div>
        </ZoneBlock>

        {/* Zone 4 — Flying */}
        <ZoneBlock zoneKey="flying" rows={zones.flying}>
          <div className="space-y-1.5">
            {zones.flying.map(r => (
              <FlyingRow
                key={r.learner_id}
                row={r}
                grade={grade}
                subject={subjectDecoded}
                metric={getStudentMetric(r.learner_id)}
                onMetricChange={(m) => setStudentMetric(r.learner_id, m)}
              />
            ))}
          </div>
        </ZoneBlock>

        </>)}  {/* end activeTab === "overview" */}
      </div>
      
      {/* Test Generator Modal */}
      <TestGeneratorModal
        open={showTestGenerator}
        onClose={() => setShowTestGenerator(false)}
        teacherId={teacherId}
        grade={gradeNum}
        preSelectedSubject={subjectDecoded}
      />

      {/* Student List Panel */}
      <StudentListPanel
        open={showStudentList}
        onClose={() => setShowStudentList(false)}
        teacherId={teacherId}
        grade={gradeNum}
        subject={subjectDecoded}
      />
    </div>
  );
}
